import { Checkbox, Collapse } from 'antd';
import { useMemo } from 'react';
import { ADMIN_MODULE_GROUPS, ADMIN_MODULES } from '@/constants/adminModules';
import styles from './ModuleAccessPicker.module.scss';

interface ModuleAccessPickerProps {
  value: string[];
  onChange: (modules: string[]) => void;
  disabled?: boolean;
  defaultExpandAll?: boolean;
}

export function ModuleAccessPicker({
  value,
  onChange,
  disabled = false,
  defaultExpandAll = false,
}: ModuleAccessPickerProps) {
  const groupedModules = useMemo(() => {
    return ADMIN_MODULE_GROUPS.map((group) => ({
      group,
      modules: ADMIN_MODULES.filter((module) => module.group === group),
    }));
  }, []);

  const defaultActiveKey = useMemo(
    () => (defaultExpandAll ? groupedModules.map((item) => item.group) : [groupedModules[0]?.group].filter(Boolean)),
    [defaultExpandAll, groupedModules],
  );

  const toggleModule = (moduleKey: string, checked: boolean) => {
    if (checked) {
      onChange([...new Set([...value, moduleKey])]);
      return;
    }
    onChange(value.filter((key) => key !== moduleKey));
  };

  const toggleGroup = (moduleKeys: string[], checked: boolean) => {
    if (checked) {
      onChange([...new Set([...value, ...moduleKeys])]);
      return;
    }
    const remove = new Set(moduleKeys);
    onChange(value.filter((key) => !remove.has(key)));
  };

  const items = groupedModules.map(({ group, modules }) => {
    const moduleKeys = modules.map((module) => module.key);
    const selectedCount = moduleKeys.filter((key) => value.includes(key)).length;
    const allSelected = selectedCount === moduleKeys.length;
    const indeterminate = selectedCount > 0 && !allSelected;

    return {
      key: group,
      label: (
        <div className={styles.panelHead} onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            disabled={disabled}
            onChange={(event) => toggleGroup(moduleKeys, event.target.checked)}
          >
            <span className={styles.groupTitle}>{group}</span>
          </Checkbox>
          <span className={styles.groupCount}>
            {selectedCount}/{moduleKeys.length}
          </span>
        </div>
      ),
      children: (
        <div className={styles.moduleGrid}>
          {modules.map((module) => (
            <label key={module.key} className={styles.moduleItem}>
              <Checkbox
                checked={value.includes(module.key)}
                disabled={disabled}
                onChange={(event) => toggleModule(module.key, event.target.checked)}
              />
              <span className={styles.moduleLabel}>{module.label}</span>
            </label>
          ))}
        </div>
      ),
    };
  });

  return (
    <Collapse
      className={styles.collapse}
      defaultActiveKey={defaultActiveKey}
      items={items}
      bordered={false}
    />
  );
}
